<script setup lang="ts">
import { ref, onMounted, onUnmounted, nextTick, useTemplateRef } from 'vue'

import { type WithAttributes, type Labeled, type Activated, toHtml, fw } from './attributes'
import { useContext } from './context'

// #region props
const {
  uiBlockSize = useContext().value.uiBlockSize ?? '48',
  uiOvershootNone = useContext().value.uiOvershootNone,
  uiRaised = useContext().value.uiRaised ?? undefined,
  ...props
} = defineProps<WithAttributes<{
  class: {
    align?: 'align-start' | 'align-stretch'
    justify?: 'justify-start' | 'justify-stretch'
    color?: `${'color-primary' | 'color-destructive' | 'color-default' | 'color-yellow' | 'color-green'}-solid` | `${'color-primary' | 'color-destructive' | 'color-default' | 'color-yellow' | 'color-green'}-ghost` | `${'color-yellow' | 'color-green'}-deep-solid` | `${'color-yellow' | 'color-green'}-deep-ghost` | 'color-accent' | 'color-default'
    shape?: 'shape-circle' | 'shape-square' | 'shape-field-grow' | 'shape-field-minimal' | 'shape-field-full'
  }
  /**
   * Indicates whether an element is being updated in the background and will not respond to user interactions.
   * Note: Use string 'false' to convey 'aria-busy may happen', otherwise use booleans for developer convenience.
   * @see https://www.w3.org/TR/wai-aria-1.2/#aria-busy
   */
  ariaBusy?: 'true' | 'false' | boolean

  /**
   * Indicates element that is visually and semantically current when element is part of a set of related elements
   * - `true`: Current.
   * - `'step'`: Current step in a multi-step process.
   * - `'location'`: Current location within an environment or context.
   * - `'date'`: Current date.
   * - `'time'`: Current time.
   * @see https://www.w3.org/TR/wai-aria-1.2/#aria-current
   */
  ariaCurrent?: boolean | 'step' | 'location' | 'date' | 'time'

  /**
   * Move focus to this component when it is mounted. Move focus back to previous when it is unmounted.
   */
  autofocus?: boolean

  /**
   * Use for buttons that the user expects at a certain position, for example in a toolbar or in a row of actions
   * Otherwise, remove the button instead.
   */
  disabled?: boolean

  ariaDisabled?: 'true' | 'false' | boolean

  uiBlockSize?: '24' | '40' | '48'

  /**
   * Use in a context where you want to prevent the default overlapping of round edges
   */
  uiOvershootNone?: boolean

  uiRaised?: boolean

  /**
   * Default to `submit` in the absence of `@click`
   * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button#type
   */
  onClick?: (e: MouseEvent) => void
} & Labeled & Activated>>()
// #endregion props

// -------------- Autofocus -------------- //
//
// TODO: Use v-focus directive for in-template declarative focus

const previouslyFocusedElement = ref()
const button = useTemplateRef('button')

onMounted(() => props.autofocus && nextTick(() => {
  previouslyFocusedElement.value = document.activeElement
  previouslyFocusedElement.value?.blur()
  button.value?.focus()
}))

onUnmounted(() =>
  previouslyFocusedElement.value?.focus()
)
</script>

<template>
  <button
    ref="button"
    :type="onClick ? 'button' : 'submit'"
    v-bind="toHtml<'button'>(props)"
    :class="[
      fw.layout.row,
      props.class,
      // Default classes:
      {
        [fw.justify.center]: !props.class?.includes('justify-'),
        [fw.shape.field]: !props.class?.includes('shape-'),
        [fw.color.defaultSolid]: !props.class?.includes('color-'),
      },
      fw.font.captionEmphasized,
      $style.button,
    ]"
    :ui-block-size
    :ui-overshoot-none
    :ui-raised
    :aria-disabled="disabled"
    :onClick
  >
    <slot :button />
  </button>
</template>

<style module>
.button {
  /**
  * ====================================================
  * Color transitions
  * ====================================================
  */

  transition: background-color .3s;

  &:hover, &:focus-visible {
      transition: none;
  }

  &:is([aria-pressed=true], [aria-expanded=true], [aria-selected=true], [aria-checked=true]):hover:not(:active){
          transition: background-color .3s;
          [ui-nowrap] {
              flex-wrap: nowrap;
              text-wrap: nowrap;
          }
          [ui-span-all] {
              grid-column: 1 / -1;
          }
  }

  /**
  * ====================================================
  * Layout
  * ====================================================
  */

  position: relative;

  gap: 8px;

  /* Badge placement */
  --badge-inset: -3px -3px auto auto;

  &[ui-block-size='40'] {
  --badge-inset: -4px -4px auto auto;
}

  &[ui-block-size='24'] {
  --badge-inset: -10px -10px auto auto;
}

  &:global(.shape-circle) {
    --badge-inset: -4px -4px auto auto;

    &[ui-block-size='40'] {
    padding: 0 4px;
    --badge-inset: -5px -5px auto auto;
  }

    &[ui-block-size='24'] {
    padding: 0 4px;
    --badge-inset: -9px -9px auto auto;
  }
  }

  border: 0;

  /**
  * ====================================================
  * Interactivity
  * ====================================================
  */

  /* Add transparent hit area at least 44px squared to satisfy WCAG2 target size requirement AAA: */
  &::after {
    content: '';
    position: absolute;

    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);

    width: max(48px, 100%);
    height: max(48px, 100%);
  }
}

/**
* ====================================================
* Indicate aria-busy
* ====================================================
*/
.button[aria-busy="true"] {
  animation: shimmer 1s ease-in-out alternate infinite;
}

@keyframes shimmer {
  from { opacity: 1; }
  to { opacity: .4; }
}

/**
* ====================================================
* Split buttons: Place several buttons into a <span>
* ====================================================
*/
span:has(.button + .button) {
  display: flex;
  flex-wrap: nowrap;

  > .button {
    min-inline-size: 40px;
  }

  /* Reset outer radii for all but first/last */
  > .button:not(:first-child) {
    border-start-start-radius: 0;
    border-end-start-radius: 0;
  }

  > .button:not(:last-child) {
    border-start-end-radius: 0;
    border-end-end-radius: 0;
  }

  /**
   * 1. Base Overlay Definition
   * Every button gets an overlay spanning the gap.
   */
  > .button::before {
    content: '';
    position: absolute;
    inset: 20% -1px;
    pointer-events: none;

    border-style: solid;
    border-width: 0 2px;

    border-inline-start-color: color-mix(in oklab, currentcolor 20%, transparent);
    border-inline-end-color: color-mix(in oklab, currentcolor 25%, transparent);
  }

  > .button:is([aria-pressed=true], :active, :hover, [class*="-solid"])::before {
    display: none;
  }

  > .button:has(+*:is([aria-pressed=true], :active, :hover, [class*="-solid"]))::before, > .button:last-child::before {
    border-inline-end: none;
  }

  > :is([aria-pressed=true], :active, :hover, [class*="-solid"]) + .button::before, > .button:first-child::before {
    border-inline-start: none;
  }
}
</style>
